import chalk from 'chalk';
import ora from 'ora';
import {
  detectDrift,
  getFramework,
  DEFAULT_FRAMEWORK,
  type DriftReport,
  type DriftFinding,
  type RiskTier,
} from '@augmented/core';
import { readLiveState } from '@augmented/core/drift/live-state-reader.js';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { success, error, warn, info, table } from '../lib/output.js';
import { isJsonMode } from '../lib/globals.js';

const KEBAB_CASE_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

function severityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return chalk.red(severity);
    case 'warning':
      return chalk.yellow(severity);
    default:
      return chalk.dim(severity);
  }
}

function printDriftReport(report: DriftReport): void {
  console.log(chalk.bold(`\nDrift Report: ${report.codeName}\n`));

  info(`Agent ID:    ${report.agentId}`);
  info(`Checked at:  ${report.checkedAt.toISOString()}`);
  console.log();

  if (!report.hasDrift) {
    success('No drift detected. Agent matches provisioned state.');
    return;
  }

  warn(`Drift detected: ${report.criticalCount} critical, ${report.warningCount} warning`);
  console.log();

  const rows = report.findings.map((f: DriftFinding) => [
    severityColor(f.severity),
    f.category,
    f.field,
    f.message,
  ]);

  table(['Severity', 'Category', 'Field', 'Message'], rows);
}

interface DriftCheckOpts {
  json?: boolean;
  audit?: boolean;
  config?: string;
}

/**
 * `agt drift check <code-name>`
 */
export async function driftCheckCommand(
  codeName: string,
  opts: DriftCheckOpts,
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  if (!KEBAB_CASE_RE.test(codeName)) {
    error('Invalid code-name. Must be kebab-case (e.g. "my-agent").');
    process.exitCode = 1;
    return;
  }

  const useJson = opts.json || isJsonMode();

  const spinner = ora({ text: `Checking drift for "${codeName}"…`, isSilent: useJson });
  spinner.start();

  try {
    // Fetch drift data via API
    const driftData = await api.get<{
      agent: Record<string, unknown>;
      snapshot: Record<string, unknown> | null;
      charter: { raw_content: string; version: string } | null;
      tools: { raw_content: string; version: string } | null;
    }>(`/agents/${encodeURIComponent(codeName)}/drift-data`);

    const agent = driftData.agent;
    const snapshot = driftData.snapshot;

    if (!snapshot) {
      spinner.fail(`No provision snapshot found for "${codeName}". Run \`agt provision\` first.`);
      error('No provision snapshot available');
      process.exitCode = 1;
      return;
    }

    // Resolve adapter from agent's framework field
    const frameworkId = (agent.framework as string) ?? DEFAULT_FRAMEWORK;
    const adapter = getFramework(frameworkId);
    const trackedFiles = adapter.driftTrackedFiles();

    // Read live state from disk (team-scoped to prevent cross-team collisions)
    const defaultDir = `.augmented/${teamSlug}/${codeName}/provision`;
    const configPath = opts.config ?? `${defaultDir}/${trackedFiles[0]}`;

    const liveState = await readLiveState({
      configPath,
      teamDir: defaultDir,
      trackedFiles,
      runAudit: opts.audit,
    });

    // Run drift detection
    const report = detectDrift(
      {
        frameworkConfig: {} as Record<string, unknown>,
        charterHash: snapshot.charter_hash as string ?? '',
        toolsHash: snapshot.tools_hash as string ?? '',
        toolAllow: snapshot.tool_allow as string[] ?? [],
        toolDeny: snapshot.tool_deny as string[] ?? [],
        channelsConfig: (snapshot.channels_config ?? {}) as Record<string, unknown>,
        sandboxMode: snapshot.sandbox_mode as string ?? 'all',
      },
      liveState,
      agent.agent_id as string,
      codeName,
      (agent.risk_tier as RiskTier) ?? 'Medium',
    );

    spinner.stop();

    if (useJson) {
      console.log(JSON.stringify(report, null, 2));
    } else {
      printDriftReport(report);
    }

    if (report.hasDrift) {
      process.exitCode = 1;
    }
  } catch (err) {
    spinner.fail('Drift check failed.');
    error((err as Error).message);
    process.exitCode = 1;
  }
}

interface DriftWatchOpts {
  interval?: string;
  webhook?: string;
  json?: boolean;
  config?: string;
}

/**
 * `agt drift watch <code-name>`
 */
export async function driftWatchCommand(
  codeName: string,
  opts: DriftWatchOpts,
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  if (!KEBAB_CASE_RE.test(codeName)) {
    error('Invalid code-name. Must be kebab-case (e.g. "my-agent").');
    process.exitCode = 1;
    return;
  }

  const useJson = opts.json || isJsonMode();

  const intervalSec = parseInt(opts.interval ?? '60', 10);
  if (isNaN(intervalSec) || intervalSec < 1) {
    error('Interval must be a positive number of seconds.');
    process.exitCode = 1;
    return;
  }

  // Resolve agent once up front
  let agent: Record<string, unknown>;
  try {
    const data = await api.get<{ agent: Record<string, unknown> }>(`/agents/${encodeURIComponent(codeName)}`);
    agent = data.agent;
  } catch (err) {
    error((err as Error).message);
    process.exitCode = 1;
    return;
  }

  // Resolve adapter from agent's framework field
  const frameworkId = (agent.framework as string) ?? DEFAULT_FRAMEWORK;
  const adapter = getFramework(frameworkId);
  const trackedFiles = adapter.driftTrackedFiles();

  if (!useJson) {
    console.log(
      chalk.bold(`\nWatching drift for "${codeName}" every ${intervalSec}s. Press Ctrl+C to stop.\n`),
    );
  }

  let previousFindingCount = -1;

  const runCheck = async (): Promise<void> => {
    const spinner = ora({ text: `Checking drift for "${codeName}"…`, isSilent: useJson });
    spinner.start();

    try {
      const driftData = await api.get<{
        snapshot: Record<string, unknown> | null;
      }>(`/agents/${encodeURIComponent(codeName)}/drift-data`);

      const snapshot = driftData.snapshot;
      if (!snapshot) {
        spinner.warn('No provision snapshot found. Skipping check.');
        return;
      }

      const watchDefaultDir = `.augmented/${teamSlug}/${codeName}/provision`;
      const configPath = opts.config ?? `${watchDefaultDir}/${trackedFiles[0]}`;

      const liveState = await readLiveState({
        configPath,
        teamDir: watchDefaultDir,
        trackedFiles,
      });

      const report = detectDrift(
        {
          frameworkConfig: {} as Record<string, unknown>,
          charterHash: snapshot.charter_hash as string ?? '',
          toolsHash: snapshot.tools_hash as string ?? '',
          toolAllow: snapshot.tool_allow as string[] ?? [],
          toolDeny: snapshot.tool_deny as string[] ?? [],
          channelsConfig: (snapshot.channels_config ?? {}) as Record<string, unknown>,
          sandboxMode: snapshot.sandbox_mode as string ?? 'all',
        },
        liveState,
        agent.agent_id as string,
        codeName,
        (agent.risk_tier as RiskTier) ?? 'Medium',
      );

      spinner.stop();

      const isNew = report.findings.length !== previousFindingCount;
      previousFindingCount = report.findings.length;

      if (useJson) {
        console.log(JSON.stringify(report, null, 2));
      } else if (report.hasDrift) {
        printDriftReport(report);
      } else {
        success(`[${new Date().toLocaleTimeString()}] No drift detected.`);
      }

      if (isNew && report.hasDrift && opts.webhook) {
        try {
          await fetch(opts.webhook, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(report),
          });
          if (!useJson) info(`Webhook notified: ${opts.webhook}`);
        } catch (webhookErr) {
          if (!useJson) warn(`Failed to notify webhook: ${(webhookErr as Error).message}`);
        }
      }
    } catch (err) {
      spinner.fail('Drift check failed.');
      error((err as Error).message);
    }
  };

  await runCheck();

  const timer = setInterval(() => {
    void runCheck();
  }, intervalSec * 1000);

  process.on('SIGINT', () => {
    clearInterval(timer);
    if (!useJson) console.log(chalk.dim('\nStopped watching.'));
    process.exit(0);
  });
}
